import React from "react";
import { Link, Outlet, useLocation, Navigate } from "react-router-dom";
import { useMemberAuth } from "@/lib/memberAuth";
import Logo from "@/components/Logo";
import { SignOut, House, Users, CalendarDots, Handshake, UserCircle, Package, ShieldWarning, DownloadSimple, CalendarBlank, Newspaper, Storefront } from "@phosphor-icons/react";

const NAV = [
  { to: "/portal", label: "Dashboard", icon: House, exact: true, testid: "portal-nav-dashboard" },
  { to: "/portal/directory", label: "Directory", icon: Users, testid: "portal-nav-directory" },
  { to: "/portal/shipments", label: "Shipments", icon: Package, testid: "portal-nav-shipments" },
  { to: "/portal/payments", label: "Payment Monitor", icon: ShieldWarning, testid: "portal-nav-payments" },
  { to: "/portal/downloads", label: "Downloads", icon: DownloadSimple, testid: "portal-nav-downloads" },
  { to: "/portal/holidays", label: "Holidays", icon: CalendarBlank, testid: "portal-nav-holidays" },
  { to: "/portal/events", label: "Events", icon: CalendarDots, testid: "portal-nav-events" },
  { to: "/portal/matches", label: "Introductions", icon: Handshake, testid: "portal-nav-matches" },
  { to: "/portal/news", label: "News", icon: Newspaper, testid: "portal-nav-news" },
  { to: "/portal/vendors", label: "Vendors", icon: Storefront, testid: "portal-nav-vendors" },
  { to: "/portal/profile", label: "My profile", icon: UserCircle, testid: "portal-nav-profile" },
];

export default function MemberLayout() {
  const { member, booted, logout } = useMemberAuth();
  const { pathname } = useLocation();
  const isAdminPreview = !!member?.is_admin_preview;

  if (!booted) {
    return <div className="min-h-screen flex items-center justify-center text-navy/60">Loading…</div>;
  }
  if (!member) {
    return <Navigate to="/portal/login" replace />;
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex flex-col" data-testid="member-portal-layout">
      {isAdminPreview && (
        <div className="bg-ember/90 text-navy text-xs uppercase tracking-widest px-6 py-2 flex items-center justify-between gap-4" data-testid="admin-preview-banner">
          <span><strong>Admin preview mode</strong> — you're viewing the member portal as {member.email}. Filtered data (Shipments, Payment Monitor, Holidays) will be empty for your admin account.</span>
          <a href="/admin" className="uppercase tracking-widest text-navy hover:text-navy/70 whitespace-nowrap" data-testid="portal-back-to-admin">← Back to Admin</a>
        </div>
      )}
      <header className="border-b border-black/10 bg-white">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-8">
            <Logo testId="portal-logo" />
            <div className="text-[10px] uppercase tracking-[0.24em] text-navy/50 hidden sm:block">Member Portal</div>
          </div>
          <div className="flex items-center gap-4 text-sm">
            <div className="text-right hidden sm:block leading-tight">
              <div className="text-navy font-medium" data-testid="portal-current-member">{member.company_name}</div>
              <div className="text-navy/50 text-xs">{member.country}</div>
            </div>
            {isAdminPreview ? (
              <a href="/admin" data-testid="portal-exit-preview" className="text-xs uppercase tracking-widest text-navy/60 hover:text-ember flex items-center gap-1.5">
                <SignOut size={14} /> Exit preview
              </a>
            ) : (
              <button onClick={logout} data-testid="portal-logout-button" className="text-xs uppercase tracking-widest text-navy/60 hover:text-ember flex items-center gap-1.5">
                <SignOut size={14} /> Sign out
              </button>
            )}
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 w-full flex-1 grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-8 py-8">
        <aside className="lg:sticky lg:top-24 h-fit">
          <nav className="flex lg:flex-col gap-1 border border-black/10 bg-white p-2">
            {NAV.map((n) => {
              const active = n.exact ? pathname === n.to : pathname.startsWith(n.to);
              const Icon = n.icon;
              return (
                <Link
                  key={n.to}
                  to={n.to}
                  data-testid={n.testid}
                  className={`flex items-center gap-2 px-3 py-2.5 text-sm transition-colors ${active ? "bg-navy text-ember" : "text-navy/80 hover:bg-black/5"}`}
                >
                  <Icon size={16} weight={active ? "duotone" : "regular"} />
                  {n.label}
                </Link>
              );
            })}
          </nav>
          <div className="mt-4 text-[10px] uppercase tracking-widest text-navy/40 px-2">
            EPAS · epas.one
          </div>
        </aside>
        <main className="min-w-0">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
